import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DialogAppBar from "../../Components/DialogAppBar";
import {
  AWS_IMAGE_HOSTING_TAG,
  formatGoogleDriveMediaLink,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";

const initialFormState = {
  sku: "",
  name: "",
  imageUrl: "",
  costPrice: "",
  listPrice: "",
  providerName: "",
  providerEmail: "",
  providerPhone: "",
};

export default function ServiceItemDialog({
  open,
  onClose,
  onSubmit,
  onDelete,
  serviceItem,
  isSubmitting,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const server = EnvSettings.server;

  useEffect(() => {
    if (open) {
      setForm({
        sku: serviceItem?.sku || "",
        name: serviceItem?.name || "",
        imageUrl: serviceItem?.imageUrl || "",
        costPrice:
          serviceItem?.costPrice || serviceItem?.costPrice === 0
            ? String(serviceItem.costPrice)
            : "",
        listPrice:
          serviceItem?.listPrice || serviceItem?.listPrice === 0
            ? String(serviceItem.listPrice)
            : "",
        providerName: serviceItem?.providerInfo?.name || "",
        providerEmail: serviceItem?.providerInfo?.email || "",
        providerPhone: serviceItem?.providerInfo?.phone || "",
      });
      setErrors({});
    } else {
      setForm(initialFormState);
      setErrors({});
    }
    setShowDeleteDialog(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  }, [open, serviceItem]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    const trimmedSku = form.sku.trim();
    if (!trimmedSku) {
      setErrors({ sku: t("serviceItemsModule.skuRequired") });
      return;
    }
    setErrors({});
    const providerName = form.providerName ? form.providerName.trim() : "";
    const providerEmail = form.providerEmail ? form.providerEmail.trim() : "";
    const providerPhone = form.providerPhone ? form.providerPhone.trim() : "";
    const providerInfo =
      providerName || providerEmail || providerPhone
        ? {
            ...(providerName ? { name: providerName } : {}),
            ...(providerEmail ? { email: providerEmail } : {}),
            ...(providerPhone ? { phone: providerPhone } : {}),
          }
        : null;

    onSubmit({
      sku: trimmedSku,
      name: form.name,
      imageUrl: form.imageUrl,
      costPrice: form.costPrice,
      listPrice: form.listPrice,
      providerInfo,
    });
  };

  const deleteConfirmationMessage = serviceItem
    ? t("serviceItemsModule.deleteConfirmation", {
        sku: serviceItem?.sku || "",
        name: serviceItem?.name ? `(${serviceItem.name})` : "",
      })
    : "";

  const handleDeleteClick = () => {
    if (!serviceItem || !onDelete || isSubmitting) {
      return;
    }
    setShowDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  const handleDeleteConfirm = () => {
    if (!onDelete) {
      return;
    }
    onDelete();
    setShowDeleteDialog(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const handleImageUpload = () => {
    if (!selectedFile) {
      return;
    }
    const sku = form.sku?.trim() || serviceItem?.sku || "";
    if (!sku) {
      dispatch(
        showPopupSnackbar({
          error: true,
          message: t(
            "serviceItemsModule.skuRequiredForUpload",
            "Enter a SKU before uploading an image."
          ),
        })
      );
      return;
    }
    const data = new FormData();
    data.append("token", getToken());
    data.append("files[]", selectedFile);
    data.append(
      "resource",
      JSON.stringify({ type: "ServiceItem", number: sku })
    );
    setIsUploadingImage(true);
    fetch(server + "/upload_media_files", {
      method: "POST",
      body: data,
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        dispatch(showPopupSnackbar(result));
        if (!result.error) {
          const hostedUrl = `${AWS_IMAGE_HOSTING_TAG}${sku}/${selectedFile.name}`;
          setForm((prev) => ({
            ...prev,
            imageUrl: hostedUrl,
          }));
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
          }
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            error: true,
            message: t(
              "serviceItemsModule.imageUploadFailed",
              "Image upload failed."
            ),
          })
        );
      })
      .finally(() => {
        setIsUploadingImage(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby="service-item-dialog"
    >
      <DialogAppBar
        header={
          serviceItem
            ? t("serviceItemsModule.editServiceItem")
            : t("serviceItemsModule.addServiceItem")
        }
        onClose={onClose}
      />
      <DialogContent style={{ marginTop: 100, paddingBottom: 120 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("serviceItemsModule.sku")}
                  value={form.sku}
                  onChange={handleChange("sku")}
                  required
                  fullWidth
                  error={Boolean(errors.sku)}
                  helperText={errors.sku}
                  variant="outlined"
                  disabled={Boolean(serviceItem)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("serviceItemsModule.name")}
                  value={form.name}
                  onChange={handleChange("name")}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" flexDirection="column" gridGap={12}>
                  <TextField
                    label={t("serviceItemsModule.imageUrl")}
                    value={form.imageUrl}
                    onChange={handleChange("imageUrl")}
                    fullWidth
                    variant="outlined"
                  />
                  <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={12}>
                    <input
                      accept="image/*"
                      type="file"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleImageUpload}
                      disabled={!selectedFile || isUploadingImage}
                    >
                      {isUploadingImage
                        ? t("serviceItemsModule.uploadingImage", "Uploading…")
                        : t("serviceItemsModule.uploadMedia", "Upload image")}
                    </Button>
                    {selectedFile ? (
                      <Typography variant="body2">{selectedFile.name}</Typography>
                    ) : null}
                  </Box>
                  {form.imageUrl ? (
                    <Box display="flex" alignItems="center" gridGap={16}>
                      <img
                        src={formatGoogleDriveMediaLink(form.imageUrl)}
                        alt={form.name || form.sku || "service-item"}
                        style={{ width: 120, borderRadius: 6, objectFit: "cover" }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("serviceItemsModule.costPrice")}
                  value={form.costPrice}
                  onChange={handleChange("costPrice")}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("serviceItemsModule.listPrice")}
                  value={form.listPrice}
                  onChange={handleChange("listPrice")}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  {t("serviceItemsModule.providerInfoLabel")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("serviceItemsModule.providerName")}
                  value={form.providerName}
                  onChange={handleChange("providerName")}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("serviceItemsModule.providerEmail")}
                  value={form.providerEmail}
                  onChange={handleChange("providerEmail")}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("serviceItemsModule.providerPhone")}
                  value={form.providerPhone}
                  onChange={handleChange("providerPhone")}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <div
        style={{
          width: "100%",
          padding: "10px 32px",
          backgroundColor: "#f5f5f5",
          position: "fixed",
          bottom: 0,
          left: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          columnGap: 16,
        }}
      >
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          {serviceItem
            ? t("common.save")
            : t("serviceItemsModule.createServiceItem")}
        </Button>
        <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
          {t("common.cancel")}
        </Button>
        {serviceItem && onDelete ? (
          <Button
            onClick={handleDeleteClick}
            color="secondary"
            variant="contained"
            disabled={isSubmitting}
          >
            {t("serviceItemsModule.deleteServiceItem")}
          </Button>
        ) : null}
      </div>
      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="service-item-delete-title"
      >
        <DialogTitle id="service-item-delete-title">
          {t("serviceItemsModule.deleteServiceItem")}
        </DialogTitle>
        <DialogContent>{deleteConfirmationMessage}</DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            color="primary"
            variant="outlined"
            disabled={isSubmitting}
          >
            {t("no")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            variant="contained"
            disabled={isSubmitting}
          >
            {t("yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
